import { Inject, Injectable } from '@angular/core'
import { Router } from '@angular/router'
import { Actions, createEffect, ofType } from '@ngrx/effects'
import { Action } from '@ngrx/store'
import { BrandingService } from 'libs/gb-front/auction/branding/src/lib/branding.service'
import * as TotalRaisedActions from 'libs/gb-front/auction/data-access-total-raised/src/lib/+state/total-raised.actions'
import { GbAuctionHomeUrlService } from 'libs/gb-front/auction/util/src'
import * as AuthActions from 'libs/gb-front/auth/data-access/src/lib/+state/auth.actions'
import { AuthFacade } from 'libs/gb-front/auth/data-access/src/lib/+state/auth.facade'
import { GbFrontCookieService } from 'libs/gb-front/auth/util/src'
import * as GbRealtimeActions from 'libs/gb-front/core/util/src/lib/gb-realtime/gb-realtime.actions'
import * as ItemActions from 'libs/gb-front/item/data-access/src/lib/+state/items.actions'
import * as SelectedProfileActions from 'libs/gb-front/selected-profile/data-access/src/lib/+state/selected-profile.actions'
import { SelectedProfileFacade } from 'libs/gb-front/selected-profile/data-access/src/lib/+state/selected-profile.facade'
import { ProfileTypeEnum } from 'libs/gb-front/selected-profile/data-access/src/lib/+state/selected-profile.models'
import { GbAuctionClosedDialogComponent } from 'libs/gb-front/shared/feature/src/lib/gb-auction-closed-dialog/gb-auction-closed-dialog.component'
import { GbAuctionContactDialogComponent } from 'libs/gb-front/shared/feature/src/lib/gb-auction-contact-dialog/gb-auction-contact-dialog.component'
import { GbFrontDialogService } from 'libs/gb-front/shared/util/src/lib/gb-front-dialog-service/gb-front-dialog.service'
import * as HydrationActions from 'libs/shared/hydration/src/lib/+state/hydration.actions'
import { LoaderActionTypeEnum } from 'libs/shared/utils/loader/src/lib/+state/loader.models'
import { GbLoaderService } from 'libs/shared/utils/loader/src/lib/services/gb-loader.service'
import { GbNotificationService } from 'libs/shared/utils/notification/src/lib/service/gb-notification.service'
import { BehaviorSubject, of } from 'rxjs'
import { catchError, concatMap, filter, map, mergeMap, switchMap, tap, withLatestFrom } from 'rxjs/operators'
import { AuctionApiService } from './auction-api.service'
import * as AuctionActions from './auction.actions'
import { AuctionFacade } from './auction.facade'
import { AuctionStatusEnum, CookieEnum } from './auction.models'
import * as CategoriesActions from 'libs/gb-front/category/data-access/src/lib/+state/categories.actions'
import * as FavouriteItemsActions from 'libs/gb-front/item/data-access-favourite-items/src/lib/+state/favourite-items.actions'
import { RouterUtils } from 'libs/shared/utils/helpers/src/lib/router-utils'
import { CategoryConstants } from 'libs/gb-front/category/feature/src/lib/gb-front-category-routing.module'
import { ItemConstants } from 'libs/gb-front/item/feature/src/lib/item-constants'
import { MenuItemTypeEnum } from './menu-items.models'

@Injectable()
export class AuctionEffects {
  previousAuctionStatus$ = new BehaviorSubject<AuctionStatusEnum | null>(null)

  getAuctionSuccess$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AuctionActions.getAuctionSuccess),
      switchMap((action) => [
        ItemActions.clearFilteredItems(),
        ItemActions.setAllItemsStale(),
        AuctionActions.loadAuctionSuccess({ auction: action.auction }),
        AuthActions.syncCurrentUser(action.auction.url_name),
      ]),
    ),
  )

  initGbRealtimePublicChannel$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AuctionActions.loadAuctionSuccess),
      map(() => GbRealtimeActions.initPublicChannel()),
    ),
  )

  hideAuctionLoader$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(AuctionActions.loadAuctionSuccess, AuctionActions.loadAuctionsFailure),
        map(() => this.loaderService.setLoadingComplete(LoaderActionTypeEnum.Auction)),
      ),
    { dispatch: false },
  )

  reloadAuction$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AuctionActions.reloadAuction),
      withLatestFrom(this.auctionFacade.auctionUrlName$),
      mergeMap(([, urlName]) =>
        this.auctionApiService.getAuction(urlName).pipe(
          map((auction) => AuctionActions.loadAuctionSuccess({ auction })),
          catchError((error) => {
            // if status = 0 (user offline), do not dispatch failure action
            return error.status === 0 ? of() : of(AuctionActions.loadAuctionsFailure({ error }))
          }),
        ),
      ),
    ),
  )

  initAuctionBranding$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(AuctionActions.loadAuctionSuccess),
        map((action) => {
          this.brandingService.initTheme(action.auction)
        }),
      ),
    { dispatch: false },
  )

  loadTotalRaised$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AuctionActions.loadAuctionSuccess),
      withLatestFrom(this.auctionFacade.menuItemIsVisible$(MenuItemTypeEnum.TotalRaised)),
      filter(([, isTotalRaisedVisible]) => !!isTotalRaisedVisible),
      map(() => TotalRaisedActions.loadTotalRaised()),
    ),
  )

  loadAuctionsFailure$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AuctionActions.loadAuctionsFailure),
      map(() => HydrationActions.removeState()),
    ),
  )

  redirectNotFound$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(AuctionActions.loadAuctionsFailure),
        map(() => this.router.navigate(['/not-found'])),
      ),
    { dispatch: false },
  )

  updateAuctionStatus$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(AuctionActions.loadAuctionSuccess),
        filter((action) => !!action.auction.status && action.auction.status !== AuctionStatusEnum.Stopped),
        tap((action) => {
          this.previousAuctionStatus$.next(action.auction.status as AuctionStatusEnum)
        }),
      ),
    { dispatch: false },
  )

  resyncItemsOnAuctionClosedTransition$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AuctionActions.loadAuctionSuccess),
      withLatestFrom(this.previousAuctionStatus$),
      filter(
        ([action, previousStatus]) =>
          // user active on app while auction transitioned to closing / closed
          !!previousStatus &&
          (action.auction.status === AuctionStatusEnum.Closing || action.auction.status === AuctionStatusEnum.Stopped),
      ),
      withLatestFrom(RouterUtils.getCurrentRoute(this.router)),
      concatMap(([, currentRoute]) => {
        const actions: Action[] = [ItemActions.setAllItemsStale()]
        if (currentRoute === ItemConstants.ITEMS || currentRoute?.startsWith(`${CategoryConstants.CATEGORIES}/`)) {
          actions.push(ItemActions.resyncItemsCurrentPage())
        } else if (currentRoute === CategoryConstants.CATEGORIES) {
          actions.push(CategoriesActions.setAllCategoriesStale(), CategoriesActions.resyncCategoriesCurrentPage())
        } else if (currentRoute === ItemConstants.FAVOURITES) {
          actions.push(
            FavouriteItemsActions.setAllFavouriteItemsStale(),
            FavouriteItemsActions.resyncFavouriteItemsCurrentPage(),
          )
        }
        return actions
      }),
    ),
  )

  showAuctionClosedDialog$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(AuctionActions.loadAuctionSuccess),
        withLatestFrom(this.previousAuctionStatus$),
        filter(
          ([action, previousStatus]) =>
            // user active on app while auction transitioned to closed WOW
            !!previousStatus && action.auction.status === AuctionStatusEnum.Stopped,
        ),
        tap(([action]) => {
          this.previousAuctionStatus$.next(null)
          if (action.auction.enable_app_checkout_popup) {
            this.gbFrontDialogService.openDialog(GbAuctionClosedDialogComponent)
          }
        }),
      ),
    { dispatch: false },
  )

  redirectHomePage$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(AuctionActions.redirectHomePage),
        withLatestFrom(this.gbAuctionHomeUrlService.homeUrlFragments$),
        map(([action, homeUrlFragments]) => {
          let extra = {}
          // home guard passed the skip = true
          // that will be checked on the init-guard and it will skip the auction api check
          // as that has been alreary done and it will not dispatch
          // all the auction initialisation actions again
          if (action.skip) {
            extra = {
              fragment: 'skip',
            }
          }
          this.router.navigate(homeUrlFragments, extra).then(() => {
            // when we pass the skip we have to remove that from the url
            if (action.skip) {
              const newUrl = window.location.href.split('#')[0]
              // change the url without reload the page
              window.history.pushState('', '', newUrl)
            }
          })
        }),
      ),
    { dispatch: false },
  )

  contactOrganizer$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(AuctionActions.contactOrganizer),
      withLatestFrom(this.authFacade.authProfile$),
      switchMap(([, authProfile]) => {
        const actions: Action[] = []
        const onSuccess = () => AuctionActions.openContactOrganizerDialog()

        if (!authProfile || authProfile.uuid === '') {
          actions.push(AuthActions.openRegisterLoginDialog({ checkProfile: false, onSuccess }))
        } else if (authProfile.type === ProfileTypeEnum.Guest) {
          actions.push(onSuccess())
        }

        return actions
      }),
    )
  })

  openContactOrganizerDialog$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(AuctionActions.openContactOrganizerDialog),
        map(() => this.gbFrontDialogService.openDialog(GbAuctionContactDialogComponent)),
      ),
    { dispatch: false },
  )

  contactOrganizerSend$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AuctionActions.contactOrganizerSend),
      withLatestFrom(this.auctionFacade.auctionUrlName$),
      tap(() => this.loaderService.showLoaderDialog(LoaderActionTypeEnum.ContactOrganizer)),
      mergeMap(([action, auctionUrlName]) => this.auctionApiService.contactOrganizer(auctionUrlName, action.payload)),
      map(() => AuctionActions.contactOrganizerSendSuccess()),
      catchError(async (error) => AuctionActions.contactOrganizerSendFailure({ error })),
    ),
  )

  contactOrganizerSendSuccess$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(AuctionActions.contactOrganizerSendSuccess),
        switchMap(() => [
          this.gbFrontDialogService.closeDialog(GbAuctionContactDialogComponent),
          this.notificationService.success('AUCTION_CONTACT.MESSAGE_SENT_SUCCESS'),
        ]),
      ),
    { dispatch: false },
  )

  syncProfileOnContactOrganizerSendSuccess$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AuctionActions.contactOrganizerSendSuccess),
      withLatestFrom(this.selectedProfileFacade.selectedProfile$),
      switchMap(([, selectedProfile]) => {
        const actions: Action[] = []

        if (!selectedProfile?.email) {
          actions.push(SelectedProfileActions.syncProfile({}))
        }
        return actions
      }),
    ),
  )

  contactOrganizerSendFailure$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(AuctionActions.contactOrganizerSendFailure),
        map((action) => this.notificationService.error(action.error)),
      ),
    { dispatch: false },
  )

  hideContactOrganizerLoaderDialog$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(AuctionActions.contactOrganizerSendSuccess, AuctionActions.contactOrganizerSendFailure),
        map(() => this.loaderService.hideLoaderDialog(LoaderActionTypeEnum.ContactOrganizer)),
      ),
    { dispatch: false },
  )

  acceptCookieConsent$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(AuctionActions.acceptCookieConsent),
        map(() => {
          const now = new Date()
          const expires = new Date(now.setMonth(now.getMonth() + 6))

          this.gbFrontCookieService.setCookie(CookieEnum.CookiesConsent, 'true', expires)
        }),
      ),
    { dispatch: false },
  )

  initSelectedPaymentMethod$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AuctionActions.initSelectedPaymentMethod),
      withLatestFrom(this.auctionFacade.paymentMethodsByAuthProfile$),
      switchMap(([action, paymentMethods]) => {
        const actions: Action[] = [AuctionActions.clearSelectedPaymentMethod()]
        if (action.paymentAmount && paymentMethods.length === 1) {
          actions.push(AuctionActions.setSelectedPaymentMethod({ paymentMethodUuid: paymentMethods[0].uuid }))
        }
        return actions
      }),
    ),
  )

  destroyAuction$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AuctionActions.destroyAuction),
      map(() => this.brandingService.clearTheme()),
      switchMap(() => [ItemActions.clearItemsState()]),
    ),
  )

  constructor(
    private actions$: Actions,
    private auctionApiService: AuctionApiService,
    private brandingService: BrandingService,
    private authFacade: AuthFacade,
    private selectedProfileFacade: SelectedProfileFacade,
    @Inject(AuctionFacade) private auctionFacade: AuctionFacade,
    private router: Router,
    private loaderService: GbLoaderService,
    private notificationService: GbNotificationService,
    private gbFrontDialogService: GbFrontDialogService,
    private gbAuctionHomeUrlService: GbAuctionHomeUrlService,
    private gbFrontCookieService: GbFrontCookieService,
  ) {}
}
